import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  cambiarLote,
  casarFicheros,
  ColaDeSubida,
  crearLote,
  type FicheroEnPantalla,
  fusionar,
  type LoteDelNucleo,
  loteAbierto,
  subirFichero,
  terminado,
  verLote,
  type Visibilidad,
} from '../api/lotes'
import { maximoPorFicheroDe } from '../api/medios'
import { ErrorDeLaApi } from '../api/nucleo'
import { hayQueReducir, reducirSiHaceFalta } from '../api/reducir'

/**
 * La subida por lotes en la pantalla (plan del CMS dinámico §13): vive en la página de Medios, no
 * en el diálogo, para que cerrar el diálogo no corte nada. Pasos:
 *
 * 1. las imágenes que pasan del máximo por fichero del plan se reducen antes (como hasta ahora);
 * 2. se crea el lote con lo que se va a subir; lo que el núcleo rechaza ya ahí (un tipo que no
 *    vale, un documento público en un plan que no lo admite) sale como fallido con su motivo, y
 *    el resto sigue;
 * 3. se sube de tres en tres, con el progreso de cada uno;
 * 4. el núcleo analiza y procesa en su cola, y la pantalla le pregunta cada segundo y medio.
 *
 * Pausar no corta lo que está subiendo; cancelar, sí, y el núcleo se lleva lo que no terminó.
 */
export type OpcionesDeLote = { visibilidad: Visibilidad; carpeta: string; subirIgualmente: boolean }

type Entrada = FicheroEnPantalla & { clave: string }

export type Fase = 'cancelado' | 'creando' | 'inactiva' | 'pausado' | 'subiendo' | 'terminado'

const claveDe = (f: File, i: number) => `${i}:${f.name}:${f.size}`

export const useSubidaPorLotes = (sitio: number, espacio: number) => {
  const clienteDeConsultas = useQueryClient()
  const [lote, setLote] = useState<LoteDelNucleo | null>(null)
  const [entradas, setEntradas] = useState<Entrada[]>([])
  const [fase, setFase] = useState<Fase>('inactiva')
  const [error, setError] = useState<null | string>(null)
  const cola = useRef<ColaDeSubida | null>(null)
  const ficheros = useRef(new Map<number, File>())

  const cambiar = useCallback((indice: number, cambio: Partial<FicheroEnPantalla>) => {
    setEntradas((todas) => todas.map((e) => (e.indice === indice ? { ...e, ...cambio } : e)))
  }, [])

  const progreso = useQuery({
    queryKey: ['lote-de-medios', lote?.id],
    enabled: Boolean(lote),
    queryFn: () => verLote(lote!.id),
    refetchInterval: (q) => (q.state.data && !loteAbierto(q.state.data.lote) ? false : 1500),
  })

  // Lo que dice el núcleo del lote, y cuándo se da por terminado.
  const datos = progreso.data
  useEffect(() => {
    if (datos?.lote.estado === 'terminado' && fase !== 'terminado') setFase('terminado')
  }, [datos, fase])

  const subirUno = useCallback(
    async (idLote: number, indice: number, senal: AbortSignal) => {
      const fichero = ficheros.current.get(indice)
      if (!fichero) return
      cambiar(indice, { estado: 'subiendo', progreso: 0, motivo: null })
      try {
        const r = await subirFichero(idLote, indice, fichero, fichero.name, (p) => cambiar(indice, { progreso: p }), senal)
        cambiar(indice, { estado: 'analizando', progreso: 1, medio: r.medio })
      } catch (fallo) {
        if ((fallo as Error).name === 'AbortError') return cambiar(indice, { estado: 'cancelado' })
        // En pausa: se queda pendiente, y al reanudar se vuelve a subir.
        if (fallo instanceof ErrorDeLaApi && fallo.status === 409 && /pausa/.test(fallo.message)) return cambiar(indice, { estado: 'pendiente', progreso: 0 })
        cambiar(indice, { estado: 'fallido', motivo: fallo instanceof Error ? fallo.message : 'No se ha podido subir.' })
      }
    },
    [cambiar],
  )

  const arrancarCola = useCallback(
    (idLote: number, indices: number[]) => {
      cola.current = new ColaDeSubida(indices, (i, senal) => subirUno(idLote, i, senal))
      setFase('subiendo')
      void cola.current.empezar().then(() => {
        void progreso.refetch()
      })
    },
    [subirUno, progreso],
  )

  /** Empieza un lote nuevo con estos ficheros. */
  const empezar = useCallback(
    async (elegidos: File[], o: OpcionesDeLote) => {
      setError(null)
      setFase('creando')
      const iniciales: Entrada[] = elegidos.map((f, i) => ({ clave: claveDe(f, i), indice: -1 - i, nombre: f.name, tamano: f.size, estado: 'pendiente', progreso: 0, motivo: null, medio: null, url: null }))
      setEntradas(iniciales)
      // 1. Reducir lo que no cabe en el máximo por fichero del plan (de una en una: una captura decodificada son unos 100 MB).
      const maximo = await maximoPorFicheroDe(espacio)
      const listos: File[] = []
      for (const [i, f] of elegidos.entries()) {
        if (!hayQueReducir(f, maximo)) {
          listos.push(f)
          continue
        }
        setEntradas((t) => t.map((e, j) => (j === i ? { ...e, estado: 'reduciendo' } : e)))
        const reducido = await reducirSiHaceFalta(f, maximo)
        listos.push(reducido)
        setEntradas((t) => t.map((e, j) => (j === i ? { ...e, estado: 'pendiente', nombre: reducido.name, tamano: reducido.size } : e)))
      }
      // 2. Crear el lote; lo que el núcleo rechaza por adelantado sale con su motivo y el resto sigue.
      let restantes = listos.map((f, i) => ({ f, i }))
      const rechazados = new Map<number, string>()
      for (let vuelta = 0; vuelta < 2 && restantes.length > 0; vuelta += 1) {
        try {
          const { lote: creado } = await crearLote({ sitio, ficheros: restantes.map((r) => r.f), ...o })
          ficheros.current = new Map(restantes.map((r, n) => [n, r.f]))
          setEntradas((t) =>
            t.map((e, j) => {
              const n = restantes.findIndex((r) => r.i === j)
              if (n >= 0) return { ...e, indice: n }
              return { ...e, estado: 'fallido', motivo: rechazados.get(j) ?? e.motivo }
            }),
          )
          setLote(creado)
          arrancarCola(creado.id, restantes.map((_, n) => n))
          void clienteDeConsultas.invalidateQueries({ queryKey: ['lotes-abiertos', sitio] })
          return
        } catch (fallo) {
          const lista = fallo instanceof ErrorDeLaApi && Array.isArray(fallo.datos.rechazados) ? (fallo.datos.rechazados as Array<{ indice: number; motivo: string }>) : null
          if (!lista || lista.length === 0) {
            setError(fallo instanceof Error ? fallo.message : 'No se ha podido empezar la subida.')
            setEntradas((t) => t.map((e) => ({ ...e, estado: 'fallido', motivo: e.motivo ?? null })))
            setFase('inactiva')
            return
          }
          for (const r of lista) {
            const original = restantes[r.indice]
            if (original) rechazados.set(original.i, r.motivo)
          }
          restantes = restantes.filter((_, n) => !lista.some((r) => r.indice === n))
        }
      }
      setEntradas((t) => t.map((e, j) => ({ ...e, estado: 'fallido', motivo: rechazados.get(j) ?? e.motivo })))
      setFase('inactiva')
    },
    [sitio, espacio, arrancarCola, clienteDeConsultas],
  )

  const pausar = useCallback(async () => {
    if (!lote) return
    cola.current?.pausar()
    setFase('pausado')
    await cambiarLote(lote.id, 'pausar').catch((f: unknown) => setError(f instanceof Error ? f.message : null))
    void progreso.refetch()
  }, [lote, progreso])

  const reanudar = useCallback(async () => {
    if (!lote) return
    try {
      await cambiarLote(lote.id, 'reanudar')
      setFase('subiendo')
      const quedan = entradas.filter((e) => e.indice >= 0 && e.estado === 'pendiente').map((e) => e.indice)
      if (cola.current) await cola.current.reanudar(quedan)
      else arrancarCola(lote.id, quedan)
      void progreso.refetch()
    } catch (f) {
      setError(f instanceof Error ? f.message : 'No se ha podido reanudar.')
    }
  }, [lote, entradas, arrancarCola, progreso])

  const cancelar = useCallback(async () => {
    if (!lote) return
    cola.current?.cancelar()
    setFase('cancelado')
    await cambiarLote(lote.id, 'cancelar').catch(() => undefined)
    setEntradas((t) => t.map((e) => (terminado(e.estado) ? e : { ...e, estado: 'cancelado' })))
    void clienteDeConsultas.invalidateQueries({ queryKey: ['medios', sitio] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['lotes-abiertos', sitio] })
  }, [lote, clienteDeConsultas, sitio])

  /**
   * Retomar un lote que se quedó a medias con la pestaña cerrada: el navegador ya no tiene los
   * ficheros, así que se eligen otra vez y se casan por nombre y tamaño con los que faltan. Lo que
   * se redujo al subir se vuelve a reducir para casarlo.
   */
  const retomar = useCallback(
    async (idLote: number, elegidos: File[]): Promise<{ casados: number; sinCasar: string[] }> => {
      setError(null)
      const estado = await verLote(idLote)
      const faltan = estado.ficheros.filter((f) => f.estado === 'pendiente').map((f) => f.indice)
      let { casados, sinCasar } = casarFicheros(estado.lote.ficheros, faltan, elegidos)
      if (sinCasar.length > 0) {
        const maximo = await maximoPorFicheroDe(espacio)
        const reducidos = await Promise.all(sinCasar.map((f) => reducirSiHaceFalta(f, maximo)))
        const otra = casarFicheros(estado.lote.ficheros, faltan.filter((i) => !casados.has(i)), reducidos)
        for (const [i, f] of otra.casados) casados.set(i, f)
        sinCasar = otra.sinCasar
      }
      ficheros.current = new Map(casados)
      setLote(estado.lote)
      setEntradas(
        estado.ficheros.map((f) => ({
          clave: `${f.indice}:${f.nombre}`,
          indice: f.indice,
          nombre: f.nombre,
          tamano: f.tamano,
          estado: f.estado === 'pendiente' && !casados.has(f.indice) ? 'pendiente' : f.estado,
          progreso: f.estado === 'pendiente' ? 0 : 1,
          motivo: f.estado === 'pendiente' && !casados.has(f.indice) ? 'Elige este fichero otra vez para subirlo.' : f.error,
          medio: f.medio,
          url: f.url,
        })),
      )
      if (estado.lote.estado === 'pausado' || estado.lote.estado === 'pendiente') await cambiarLote(idLote, 'reanudar')
      arrancarCola(idLote, [...casados.keys()])
      return { casados: casados.size, sinCasar: sinCasar.map((f) => f.name) }
    },
    [espacio, arrancarCola],
  )

  /** Vuelve a empezar de cero (otra subida). */
  const limpiar = useCallback(() => {
    cola.current = null
    ficheros.current = new Map()
    setLote(null)
    setEntradas([])
    setFase('inactiva')
    setError(null)
  }, [])

  const vistas = fusionar(entradas, datos?.ficheros)
  const cuentas = {
    total: vistas.length,
    listos: vistas.filter((f) => f.estado === 'listo').length,
    fallidos: vistas.filter((f) => f.estado === 'fallido' || f.estado === 'bloqueado').length,
    duplicados: vistas.filter((f) => f.estado === 'duplicado').length,
    enCurso: vistas.filter((f) => !terminado(f.estado)).length,
  }
  // Si ya no queda nada en marcha (lo que no llegó a subirse falló en el navegador y el núcleo no lo
  // cuenta), la subida está terminada para quien mira, aunque el lote siga abierto: lo que falta sale
  // después en «sin terminar de subir» y se puede volver a elegir.
  const faseVisible: Fase = fase === 'subiendo' && vistas.length > 0 && cuentas.enCurso === 0 ? 'terminado' : fase
  // Al terminar, la biblioteca, la cuota y los abiertos se vuelven a pedir: ya hay ficheros nuevos.
  const avisado = useRef<null | number>(null)
  useEffect(() => {
    if (faseVisible !== 'terminado' || !lote || avisado.current === lote.id) return
    avisado.current = lote.id
    void clienteDeConsultas.invalidateQueries({ queryKey: ['medios', sitio] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['medios-uso', espacio] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['lotes-abiertos', sitio] })
  }, [faseVisible, lote, clienteDeConsultas, sitio, espacio])
  return { lote: datos?.lote ?? lote, ficheros: vistas, fase: faseVisible, error, cuentas, empezar, pausar, reanudar, cancelar, retomar, limpiar }
}

export type SubidaPorLotes = ReturnType<typeof useSubidaPorLotes>
